import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const CoachingContext = createContext();

export const useCoaching = () => {
  const context = useContext(CoachingContext);
  if (!context) throw new Error('useCoaching doit être utilisé dans un CoachingProvider');
  return context;
};

export const CoachingProvider = ({ children }) => {
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // ── Entrepreneurs ──────────────────────────────────────────────────────────

  const fetchEntrepreneurs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/coaching/entrepreneurs');
      setEntrepreneurs(data.entrepreneurs || []);
    } catch (err) {
      toast.error('Erreur chargement entrepreneurs');
    } finally {
      setLoading(false);
    }
  }, []);

  const createEntrepreneur = useCallback(async (payload) => {
    const { data } = await axios.post('/api/coaching/entrepreneurs', payload);
    setEntrepreneurs(prev => [data.entrepreneur, ...prev]);
    toast.success('Entrepreneur créé');
    return data.entrepreneur;
  }, []);

  const updateEntrepreneur = useCallback(async (id, payload) => {
    const { data } = await axios.put(`/api/coaching/entrepreneurs/${id}`, payload);
    setEntrepreneurs(prev => prev.map(e => e.id === id ? data.entrepreneur : e));
    toast.success('Entrepreneur mis à jour');
    return data.entrepreneur;
  }, []);

  const deleteEntrepreneur = useCallback(async (id) => {
    await axios.delete(`/api/coaching/entrepreneurs/${id}`);
    setEntrepreneurs(prev => prev.filter(e => e.id !== id));
    toast.success('Entrepreneur supprimé');
  }, []);

  // ── Plans ──────────────────────────────────────────────────────────────────

  const fetchPlans = useCallback(async (entrepreneurId) => {
    setLoading(true);
    try {
      const params = entrepreneurId ? { entrepreneur_id: entrepreneurId } : {};
      const { data } = await axios.get('/api/coaching/plans', { params });
      setPlans(data.plans || []);
    } catch (err) {
      toast.error('Erreur chargement plans');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlan = useCallback(async (id) => {
    const { data } = await axios.get(`/api/coaching/plans/${id}`);
    return data.plan;
  }, []);

  const createPlan = useCallback(async (payload) => {
    const { data } = await axios.post('/api/coaching/plans', payload);
    setPlans(prev => [data.plan, ...prev]);
    toast.success('Plan créé');
    return data.plan;
  }, []);

  const updatePlan = useCallback(async (id, payload) => {
    const { data } = await axios.put(`/api/coaching/plans/${id}`, payload);
    setPlans(prev => prev.map(p => p.id === id ? data.plan : p));
    toast.success('Plan mis à jour');
    return data.plan;
  }, []);

  const deletePlan = useCallback(async (id) => {
    await axios.delete(`/api/coaching/plans/${id}`);
    setPlans(prev => prev.filter(p => p.id !== id));
    toast.success('Plan supprimé');
  }, []);

  const createAction = useCallback(async (planId, payload) => {
    const { data } = await axios.post(`/api/coaching/plans/${planId}/actions`, payload);
    toast.success('Action ajoutée');
    return data.action;
  }, []);

  const updateAction = useCallback(async (id, payload) => {
    const { data } = await axios.put(`/api/coaching/actions/${id}`, payload);
    toast.success('Action mise à jour');
    return data.action;
  }, []);

  const deleteAction = useCallback(async (id) => {
    await axios.delete(`/api/coaching/actions/${id}`);
    toast.success('Action supprimée');
  }, []);

  // ── Séances (PV) ───────────────────────────────────────────────────────────

  const fetchSessions = useCallback(async (entrepreneurId) => {
    setLoading(true);
    try {
      const params = entrepreneurId ? { entrepreneur_id: entrepreneurId } : {};
      const { data } = await axios.get('/api/coaching/sessions', { params });
      setSessions(data.sessions || []);
    } catch (err) {
      toast.error('Erreur chargement séances');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSession = useCallback(async (id) => {
    const { data } = await axios.get(`/api/coaching/sessions/${id}`);
    return data.session;
  }, []);

  const createSession = useCallback(async (payload) => {
    const { data } = await axios.post('/api/coaching/sessions', payload);
    setSessions(prev => [data.session, ...prev]);
    toast.success('Séance créée');
    return data.session;
  }, []);

  const updateSession = useCallback(async (id, payload) => {
    const { data } = await axios.put(`/api/coaching/sessions/${id}`, payload);
    setSessions(prev => prev.map(s => s.id === id ? data.session : s));
    toast.success('Séance mise à jour');
    return data.session;
  }, []);

  const deleteSession = useCallback(async (id) => {
    await axios.delete(`/api/coaching/sessions/${id}`);
    setSessions(prev => prev.filter(s => s.id !== id));
    toast.success('Séance supprimée');
  }, []);

  const createSessionAxe = useCallback(async (sessionId, payload) => {
    const { data } = await axios.post(`/api/coaching/sessions/${sessionId}/axes`, payload);
    return data.axe;
  }, []);

  const updateSessionAxe = useCallback(async (id, payload) => {
    const { data } = await axios.put(`/api/coaching/session-axes/${id}`, payload);
    return data.axe;
  }, []);

  const deleteSessionAxe = useCallback(async (id) => {
    await axios.delete(`/api/coaching/session-axes/${id}`);
  }, []);

  // ── Indicateurs ────────────────────────────────────────────────────────────

  const fetchIndicators = useCallback(async (entrepreneurId) => {
    setLoading(true);
    try {
      const params = entrepreneurId ? { entrepreneur_id: entrepreneurId } : {};
      const { data } = await axios.get('/api/coaching/indicators', { params });
      setIndicators(data.indicators || []);
    } catch (err) {
      toast.error('Erreur chargement indicateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchIndicatorValues = useCallback(async (indicatorId) => {
    const { data } = await axios.get(`/api/coaching/indicators/${indicatorId}/values`);
    return data.values || [];
  }, []);

  const createIndicator = useCallback(async (payload) => {
    const { data } = await axios.post('/api/coaching/indicators', payload);
    setIndicators(prev => [...prev, data.indicator]);
    toast.success('Indicateur créé');
    return data.indicator;
  }, []);

  const updateIndicator = useCallback(async (id, payload) => {
    const { data } = await axios.put(`/api/coaching/indicators/${id}`, payload);
    setIndicators(prev => prev.map(i => i.id === id ? data.indicator : i));
    toast.success('Indicateur mis à jour');
    return data.indicator;
  }, []);

  const deleteIndicator = useCallback(async (id) => {
    await axios.delete(`/api/coaching/indicators/${id}`);
    setIndicators(prev => prev.filter(i => i.id !== id));
    toast.success('Indicateur supprimé');
  }, []);

  const saveIndicatorValue = useCallback(async (indicatorId, payload) => {
    const { data } = await axios.post(`/api/coaching/indicators/${indicatorId}/values`, payload);
    return data.value;
  }, []);

  // ── Commentaires ───────────────────────────────────────────────────────────

  const fetchComments = useCallback(async (entityType, entityId) => {
    const { data } = await axios.get(`/api/coaching/comments/${entityType}/${entityId}`);
    return data.comments || [];
  }, []);

  const addComment = useCallback(async (payload) => {
    const { data } = await axios.post('/api/coaching/comments', payload);
    toast.success('Commentaire ajouté');
    return data.comment;
  }, []);

  // ── Statistiques ───────────────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/coaching/stats');
      setStats(data.stats);
      return data.stats;
    } catch (err) {
      toast.error('Erreur chargement statistiques');
    }
  }, []);

  return (
    <CoachingContext.Provider value={{
      entrepreneurs, plans, sessions, indicators, stats, loading,
      fetchEntrepreneurs, createEntrepreneur, updateEntrepreneur, deleteEntrepreneur,
      fetchPlans, fetchPlan, createPlan, updatePlan, deletePlan,
      createAction, updateAction, deleteAction,
      fetchSessions, fetchSession, createSession, updateSession, deleteSession,
      createSessionAxe, updateSessionAxe, deleteSessionAxe,
      fetchIndicators, fetchIndicatorValues, createIndicator, updateIndicator, deleteIndicator, saveIndicatorValue,
      fetchComments, addComment,
      fetchStats,
    }}>
      {children}
    </CoachingContext.Provider>
  );
};
